import { ForbiddenIcon, MoneyIcon } from '@nimbus-ds/icons';
import {
  Status as StatusEnum,
  FulfillmentPreferenceType as FulfillmentPreferenceTypeEnum,
} from '@tiendanube/common/src/enums';
import {
  BoxUnpackedIcon,
  TruckIcon,
  ArchiveIcon,
  ExclamationTriangleIcon,
  IconProps,
  StoreIcon,
  MailIcon,
} from '@tiendanube/icons';
import { AppearanceColorEnum } from 'commons/enums';
import getIcon from 'domains/Orders/Orders/pages/getIcon';
import isShippingPickupPoint from 'domains/Orders/Orders/utils/checkIsShippingPickupPoint';
import getFulfillmentIconStatusInfo from 'domains/Orders/Orders/utils/getFulfillmentIconStatusInfo';

interface InterfaceStatus {
  icon: React.FC<IconProps>;
  status: string;
  appearance: AppearanceColorEnum;
}

const fallback = (status: string): InterfaceStatus => ({
  icon: ExclamationTriangleIcon,
  status,
  appearance: AppearanceColorEnum.SECONDARY,
});

export const payment = (status: StatusEnum): InterfaceStatus => {
  const icon = MoneyIcon;

  switch (status) {
    case StatusEnum.AUTHORIZED:
    case StatusEnum.PENDING:
      return {
        icon,
        status: StatusEnum.PENDING,
        appearance: AppearanceColorEnum.WARNING,
      };
    case StatusEnum.PARTIALLY_PAID:
      return {
        icon,
        status: StatusEnum.PARTIALLY_PAID,
        appearance: AppearanceColorEnum.WARNING,
      };
    case StatusEnum.PAID:
      return {
        icon,
        status: StatusEnum.PAID,
        appearance: AppearanceColorEnum.SUCCESS,
      };
    case StatusEnum.VOIDED:
    case StatusEnum.WARNIG_TRANSACTIONS:
      return {
        icon,
        status: StatusEnum.VOIDED,
        appearance: AppearanceColorEnum.DANGER,
      };
    case StatusEnum.REFUNDED:
      return {
        icon,
        status: StatusEnum.REFUNDED,
        appearance: AppearanceColorEnum.SECONDARY,
      };
    case StatusEnum.PARTIALLY_REFUNDED:
      return {
        icon,
        status: StatusEnum.PARTIALLY_REFUNDED,
        appearance: AppearanceColorEnum.SECONDARY,
      };
    case StatusEnum.EXPIRED:
      return {
        icon,
        status: StatusEnum.EXPIRED,
        appearance: AppearanceColorEnum.DEFAULT,
      };
    default:
      return fallback(status);
  }
};

export const fulfillment = (
  status: StatusEnum,
  fulfillmentPreferenceType: FulfillmentPreferenceTypeEnum,
  isShippablePickup?: boolean,
): InterfaceStatus => {
  const icon = getIcon(status, fulfillmentPreferenceType);

  const isDeliveryDigital =
    fulfillmentPreferenceType ===
    FulfillmentPreferenceTypeEnum.DELIVERY_DIGITAL;

  const isNonShippable =
    fulfillmentPreferenceType === FulfillmentPreferenceTypeEnum.NON_SHIPPABLE;

  const isNewShippingPickupPoint = isShippingPickupPoint(
    fulfillmentPreferenceType,
    isShippablePickup,
  );

  switch (status) {
    case StatusEnum.UNPACKED:
      return {
        icon: isDeliveryDigital || isNonShippable ? MailIcon : BoxUnpackedIcon,
        status:
          isDeliveryDigital || isNonShippable
            ? StatusEnum.UNPACKED_DIGITAL
            : status,
        appearance:
          isDeliveryDigital || isNonShippable
            ? AppearanceColorEnum.PRIMARY
            : AppearanceColorEnum.SECONDARY,
      };
    case StatusEnum.READY_FOR_PICKUP: {
      const isPickupAndNonShippable =
        fulfillmentPreferenceType === FulfillmentPreferenceTypeEnum.PICKUP;

      return {
        icon: isPickupAndNonShippable ? StoreIcon : TruckIcon,
        status: isPickupAndNonShippable
          ? StatusEnum.PICKUP
          : StatusEnum.SHIPPED,
        appearance: isPickupAndNonShippable
          ? AppearanceColorEnum.PRIMARY
          : AppearanceColorEnum.SUCCESS,
      };
    }
    case StatusEnum.PACKED:
    case StatusEnum.UNFULFILLED:
    case StatusEnum.UNSHIPPED:
      return {
        icon,
        status:
          fulfillmentPreferenceType === FulfillmentPreferenceTypeEnum.PICKUP
            ? StatusEnum.PICKUP
            : StatusEnum.UNSHIPPED,
        appearance: AppearanceColorEnum.PRIMARY,
      };
    case StatusEnum.DISPATCHED:
    case StatusEnum.MARKED_AS_FULFILLED:
    case StatusEnum.FULFILLED:
    case StatusEnum.SHIPPED: {
      /** Using PICKUPED when dispatched is deprecated.  
    The status should now be DELIVERED. **/
      const shippedStatus =
        fulfillmentPreferenceType === FulfillmentPreferenceTypeEnum.PICKUP
          ? StatusEnum.PICKUPED
          : StatusEnum.SHIPPED;

      return {
        icon: isDeliveryDigital || isNonShippable ? MailIcon : icon,
        status:
          isDeliveryDigital || isNonShippable
            ? StatusEnum.SHIPPED_DIGITAL
            : shippedStatus,
        appearance: AppearanceColorEnum.SUCCESS,
      };
    }
    case StatusEnum.PARTIALLY_FULFILLED:
      return {
        icon,
        status: isNewShippingPickupPoint
          ? StatusEnum.PARTIALLY_PICKED_UP
          : StatusEnum.PARTIALLY_FULFILLED,
        appearance: AppearanceColorEnum.PRIMARY,
      };
    case StatusEnum.PARTIALLY_PACKED:
      return {
        icon: isDeliveryDigital ? TruckIcon : BoxUnpackedIcon,
        status: StatusEnum.PARTIALLY_PACKED,
        appearance: AppearanceColorEnum.DEFAULT,
      };
    case StatusEnum.DELIVERED: {
      const isPickup =
        fulfillmentPreferenceType === FulfillmentPreferenceTypeEnum.PICKUP;
      const isNonShippablePickup = !isShippablePickup && isPickup;
      const isNonShippableOrDigital = isNonShippable || isDeliveryDigital;
      const { fulfillmentIcon, fulfillmentStatus } =
        getFulfillmentIconStatusInfo(
          isNonShippablePickup,
          isNonShippableOrDigital,
          icon,
        );

      return {
        icon: fulfillmentIcon,
        status: fulfillmentStatus,
        appearance: AppearanceColorEnum.SUCCESS,
      };
    }
    default:
      return fallback(status);
  }
};

export const order = (status: StatusEnum): InterfaceStatus => {
  switch (status) {
    case StatusEnum.CANCELLED:
      return {
        icon: ForbiddenIcon,
        status: StatusEnum.CANCELLED,
        appearance: AppearanceColorEnum.DEFAULT,
      };
    case StatusEnum.ARCHIVED:
    case StatusEnum.CLOSED:
      return {
        icon: ArchiveIcon,
        status: StatusEnum.ARCHIVED,
        appearance: AppearanceColorEnum.DEFAULT,
      };
    default:
      return fallback(status);
  }
};
